import type { ArticuloKit, InfoArticulo, KitComponente } from "@/hooks/Api/KitsFabricacion";
import { useAppForm } from "@/hooks/useForm";
import { notificacionError } from "./Alerts";
import { FormComponente } from "./forms/FormAddComponenteKit";
import { useStore } from "@tanstack/react-form";
import { forwardRef, useImperativeHandle } from "react";

interface ImputacionFormProps {
	readonly articuloKit: ArticuloKit;
	readonly initialValues: ImputacionFormData;
	readonly onSubmit: (data: ImputacionFormData) => void;
}
export const ImputacionForm = forwardRef(function ImputacionForm(props: ImputacionFormProps, ref) {
	const formApp = useAppForm({
		defaultValues: props.initialValues,
		onSubmit: ({ value }) => {
			if (value.cantidad < 1) return notificacionError("La cantidad producida debe ser mayor a 0");
			if (!value.lote || value.lote.length < 1) return notificacionError("'Lote' no puede estar vacío");
			props.onSubmit(value);
		},
	});

	const addComponente = (articulo: InfoArticulo, cantidadPorUnidad: number, cantidadDivisor: number, prev: KitComponenteConsumo[]) => {
		const newConsumo: KitComponenteConsumo = {
			idComponente: articulo.id,
			refComponente: articulo.referencia,
			descComponente: articulo.descripcion,
			cantidadPorUnidad: cantidadPorUnidad,
			cantidadDivisor: cantidadDivisor,

			lote: "",
			cantidadDefectuosa: 0,
			cantidadDesperdiciada: 0,
		};
		return [...prev, newConsumo];
	};

	// Expose handleSubmit to parent via ref
	useImperativeHandle(ref, () => ({
		handleSubmit: () => formApp.handleSubmit()
	}));

	const cantidad = useStore(formApp.store, (state) => state.values.cantidad);


	return (
		<>
			<form className="imputacionesForm">
				<div className="cabeceraCrearImputacion">
					<div style={{ maxWidth: 100 }}>
						<formApp.AppField name="cantidad">
							{field => (
								<field.NumberField
									label="Cantidad"
									placeholder=""
									min={1}
									autoComplete="fabrication-quantity"
								/>
							)}
						</formApp.AppField>
					</div>
					<formApp.AppField name="lote">
						{field => (
							<field.TextField
								label="Lote"
								placeholder=""
								maxLength={20}
								autoComplete="fabrication-lote"
							/>
						)}
					</formApp.AppField>
					<formApp.AppField name="fecha">
						{field => (
							<field.DateField
								label="Fecha"
								placeholder=""
								autoComplete="fabrication-fecha"
							/>
						)}
					</formApp.AppField>
					<formApp.AppField name="idSeccion">
						{field => <div style={{ display: "flex", flexDirection: "column" }}>
							<label className="labelsForm">Seccion</label>
							<field.SelectSeccionField isClearable={false} />
						</div>}
					</formApp.AppField>
				</div>
				<div style={{ marginTop: 12 }}>
					<formApp.AppField name="notas">
						{field => (
							<field.TextField
								label="Notas"
								placeholder=""
								maxLength={255}
								autoComplete="fabrication-notas"
							/>
						)}
					</formApp.AppField>
				</div>
			</form>
			<div style={{ marginTop: 35 }}>
				<div className="table-responsive" style={{ marginTop: 20, borderBottom: "1px solid #111", display: "contents" }}>
					<div className="labelsForm" style={{ width: "100%" }} >Componentes</div>

					<formApp.AppField name="consumos">
						{field => (
							<TablaComponentes
								cantidad={cantidad}
								consumos={field.state.value}
								setConsumos={field.setValue}
							/>
						)}
					</formApp.AppField>
					<formApp.AppField name="consumos">
						{field => (
							<FormComponente
								nombreConjunto={props.articuloKit.descArticulo}
								componentes={field.state.value}
								addComponente={(articulo, cantidadPorUnidad, cantidadDivisor) => field.setValue(consumos => addComponente(articulo, cantidadPorUnidad, cantidadDivisor, consumos))}
							/>
						)}
					</formApp.AppField>
				</div>
			</div>
		</>
	);
});



function TablaComponentes(propss: { readonly cantidad: number; readonly consumos: KitComponenteConsumo[]; readonly setConsumos: Setter<KitComponenteConsumo[]>; }) {
	const { cantidad, consumos, setConsumos } = propss;

	return (
		<table className="dataTable display">
			<thead>
				<tr>
					<th>Componente</th>
					<th>Lote</th>
					<th>Cantidad por unidad</th>
					<th>Cantidad total</th>
					<th>Cantidad defectuosa</th>
					<th>Cantidad desperdiciada</th>
					<th></th>
				</tr>
			</thead>
			<tbody>
				{consumos.map(consumo => (
					<FilaComponente key={consumo.idComponente} cantidad={cantidad} consumo={consumo} setConsumos={setConsumos} />
				))}
			</tbody>

		</table>
	);
}
function FilaComponente(props: { readonly cantidad: number; readonly consumo: KitComponenteConsumo; readonly setConsumos: Setter<KitComponenteConsumo[]>; }) {
	const { cantidad, consumo, setConsumos } = props;

	const onChange = (field: keyof KitComponenteConsumo) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = field === "lote" ? e.target.value : e.target.valueAsNumber;
		setConsumos(prev => prev.map(c => c.idComponente === consumo.idComponente ? { ...c, [field]: value } : c));
	};
	const onRemove = () => {
		setConsumos(prev => prev.filter(c => c.idComponente !== consumo.idComponente));
	};

	return (
		<tr>
			<td>{consumo.descComponente}</td>
			<td>
				<input
					type="text"
					className="inputsForm"
					value={consumo.lote}
					onChange={onChange("lote")}
				/>
			</td>
			<td>
				<div style={{ textAlign: "center" }}>{consumo.cantidadPorUnidad}</div>
			</td>
			<td>
				<div style={{ textAlign: "center" }}>{cantidad * (consumo.cantidadPorUnidad / consumo.cantidadDivisor)}</div>
			</td>
			<td>
				<input
					type="number"
					className="inputsForm"
					value={consumo.cantidadDefectuosa}
					onChange={onChange("cantidadDefectuosa")}
				/>
			</td>
			<td>
				<input
					type="number"
					className="inputsForm"
					value={consumo.cantidadDesperdiciada}
					onChange={onChange("cantidadDesperdiciada")}
				/>
			</td>
			<td>
				<button className="eliminarComponente" type="button" onClick={onRemove}>
					<i className="fa fa-trash" />
				</button>
			</td>
		</tr>
	);
}

export interface ImputacionFormData {
	cantidad: number;
	lote: string;
	notas: string;
	fecha: string;
	idSeccion: string;

	consumos: KitComponenteConsumo[];
}
export interface KitComponenteConsumo extends KitComponente {
	lote: string;
	cantidadDefectuosa: number;
	cantidadDesperdiciada: number;
}